import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import type { Scene } from '@babylonjs/core/scene';

/**
 * Named, extensible wall textures for the room's four walls (plan/whiteboard.md's own
 * "architectural language" — skirting, picture rail — is the vocabulary this extends onto the
 * wall field between them). The owner explicitly asked for more than one, later: "Might want to
 * let users switch it up a bit to differentiate even more between multiple rooms." So this is
 * structured the same way `NodeCatalog`/`Silhouettes` is — a named vocabulary, looked up by key,
 * with an unknown key falling back to a sane default rather than throwing — rather than one
 * hardcoded pattern.
 *
 * To add a second texture: write a new `WallTextureGenerator`, add it to `WALL_TEXTURES` under
 * a new key. Nothing else has to change — `resolveWallTextureGenerator` and `buildWallTexture`
 * both already fall back to `DEFAULT_WALL_TEXTURE_KEY` for anything they don't recognise.
 *
 * Deliberately not wired to `RoomDto`, the database, or the hub: nothing yet *selects* between
 * these per room. That is a later, separate decision that touches the schema (a
 * `wallTextureKey`-shaped field on the room or its template), out of scope for this change. This
 * module only makes the client-side structure ready for that decision, per the brief that asked
 * for it — the seam is left here, deliberately, rather than guessed at.
 */

/**
 * Built once per call, from a `Scene`. Returns a small, tileable canvas texture — never a
 * texture baked to the whole wall's own size, which is exactly the "large texture" mistake
 * `SceneRoot.buildRoom`'s own wall-tiling call (below) is written to avoid. Callers are
 * responsible for repeating it across a wall's real dimensions via `uScale`/`vScale` — this
 * function only builds the one tile's own appearance.
 *
 * `transposed` exists because `CreateBox`'s default UV mapping does not treat every face the
 * same way: for a box's `+Z`/`-Z` faces (the visible face of a north/south wall) texture-`u`
 * runs along the box's local X and texture-`v` runs along local Y, but for the `+X`/`-X` faces
 * (the visible face of an east/west wall) that pairing is rotated — texture-`u` runs along local
 * Y and texture-`v` along local Z instead (confirmed empirically against a real `CreateBox`
 * call, not assumed from the source alone — see `SceneRoot.ts`'s own comment at the wall-texture
 * call site for the readout). A generator normally draws its seam-bearing axis onto texture-`v`
 * (so `SceneRoot` can tie `vScale` to the room's own `wallHeight` and get physically-spaced,
 * horizontal seams). Called with `transposed = true`, it must draw that same seam-bearing axis
 * onto texture-`u` instead — a real transpose of the canvas, not merely a relabelling — so that
 * whichever texture axis a given wall's visible face actually maps to world height is the one
 * carrying the seam. Every generator added to `WALL_TEXTURES` has to implement both orientations
 * correctly, which is why this is part of the type rather than an ad hoc parameter on one
 * generator.
 */
export type WallTextureGenerator = (scene: Scene, transposed: boolean) => DynamicTexture;

/** Physical size, in metres, of one tile of `buildPanelsWithGrainTexture`'s own pattern. Only
 *  the height matters for correctness — see that function's own comment for why — but the width
 *  is kept the same order of magnitude so the texture stays roughly square in texel terms rather
 *  than absurdly thin. */
export const PANELS_WITH_GRAIN_TILE_WIDTH_METRES = 1;
export const PANELS_WITH_GRAIN_TILE_HEIGHT_METRES = 0.6;

/**
 * Texels per metre for `buildPanelsWithGrainTexture`. Doubled from the original 160 after a
 * headset report — "a bit low res" — read up close: at 160, the tile is 160x96 texels, a texel
 * every 6.25mm, which is coarse enough at Quest's per-eye resolution and typical arm's-length
 * viewing distance to read as soft rather than crisp. At 320, a texel is 3.1mm, and the tile
 * grows to 320x192 (transposed: 192x320) — 4x the pixel count of the original, since area scales
 * with the square of the linear density. Still nowhere near "a large texture tiled": at 4 bytes
 * per texel (`DynamicTexture`'s underlying RGBA8, no mipmaps — `generateMipMaps: false` at the
 * construction call below) that is roughly 245 KB for the canonical tile and the same again for
 * the transposed one used on east/west walls (`SceneRoot.ts`'s own wall-texture comment) — under
 * half a megabyte of GPU memory total for both orientations, negligible next to the eight Draco
 * model files this project already ships. Chosen as a doubling rather than a larger jump because
 * the tile is *tiled*, not stretched — texel density and physical tile size trade off directly
 * (this module's own top comment), so doubling density without changing
 * `PANELS_WITH_GRAIN_TILE_WIDTH_METRES`/`_HEIGHT_METRES` keeps the same physical repeat spacing
 * while making each repeat sharper, rather than growing the tile itself and repeating less often
 * at the same texel density.
 */
const PANELS_WITH_GRAIN_TEXELS_PER_METRE = 320;

/** Amplitude of the grain's own per-channel jitter, in 0-255 terms. Small enough that the
 *  surface reads as "not perfectly flat" only once you look for it — the owner's own bar
 *  ("noticeable only once you look for it") — rather than as visible speckle from across the
 *  room. Tuned by eye against that bar, not derived from anything.
 */
const GRAIN_AMPLITUDE = 10;

/** How dark the seam line is drawn, in the same 0-1 alpha terms `SceneRoot.gridTexture` already
 *  uses for the floor's own lines. Far fainter than the grid's 0.35 — a seam is meant to read as
 *  "the wall is panelled", not as a drawn line competing with the skirting/picture rail trim it
 *  echoes.
 */
const SEAM_ALPHA = 0.1;

/**
 * Deterministic pseudo-random noise in [0, 1), keyed off integer texel coordinates. Not
 * `Math.random()`: this project's other hand-authored, fixed patterns (`STAR_OFFSETS_METRES` in
 * `SceneRoot.ts`) are deliberately reproducible across runs rather than reaching for real
 * randomness, and the same reasoning applies here — a wall's grain looking different every time
 * the room happens to rebuild (a reconnect's resync, most plausibly) would read as the surface
 * itself changing, not as texture. The classic sine-hash trick: cheap, has no visible periodicity
 * at the tile sizes here, and needs no seeded PRNG dependency.
 */
function pseudoRandom01(x: number, y: number): number {
  const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return s - Math.floor(s);
}

function clampByte(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

/**
 * "Panels with subtle grain" — the owner's own words for the look: faint horizontal panel
 * seams, matching the skirting/picture rail's own horizontal trim language, plus a fine grain so
 * the surface is not perfectly flat. The first, and currently only, entry in `WALL_TEXTURES`.
 *
 * Built as one small tile rather than one texture per wall (contrast `SceneRoot.gridTexture`,
 * which bakes the floor's grid at the room's own exact size): a wall's own extent varies with
 * the room template and can be much larger than a floor, so a texture sized to it would not stay
 * "modest" the way the brief asks. `SceneRoot.buildRoom` repeats this tile across each wall via
 * `uScale`/`vScale` instead.
 *
 * Only one seam is drawn, at the tile's own top edge in the untransposed orientation (y = 0):
 * tiled along the seam-bearing axis, that single line repeats into an evenly-spaced stack of
 * seams for free, at whatever spacing `PANELS_WITH_GRAIN_TILE_HEIGHT_METRES` implies once
 * `SceneRoot` sets the matching scale from the room's real wall height — no need to draw more
 * than one line into the texture itself.
 *
 * `transposed` (see `WallTextureGenerator`'s own doc comment for why this parameter exists at
 * all) genuinely swaps the canvas's width and height — a tile that is `TILE_WIDTH x TILE_HEIGHT`
 * metres untransposed becomes `TILE_HEIGHT x TILE_WIDTH` transposed — and draws the seam along
 * the canvas's left edge (x = 0), spanning the full height, instead of along the top edge
 * spanning the full width. That is a real transpose, not a relabelling: swapping which *scale*
 * constant (`uScale` vs `vScale`) a caller multiplies by cannot reorient the seam, because the
 * seam's orientation is baked into which canvas axis the line was drawn along in the first
 * place — the same "this is a signed test, not merely a number" shape CLAUDE.md's own whiteboard
 * touch-plane note describes for a different bug, but the mechanism here is UV-axis identity
 * rather than a sign.
 */
function buildPanelsWithGrainTexture(scene: Scene, transposed: boolean): DynamicTexture {
  const alongMetres = transposed ? PANELS_WITH_GRAIN_TILE_HEIGHT_METRES : PANELS_WITH_GRAIN_TILE_WIDTH_METRES;
  const acrossMetres = transposed ? PANELS_WITH_GRAIN_TILE_WIDTH_METRES : PANELS_WITH_GRAIN_TILE_HEIGHT_METRES;

  const size = {
    width: Math.round(alongMetres * PANELS_WITH_GRAIN_TEXELS_PER_METRE),
    height: Math.round(acrossMetres * PANELS_WITH_GRAIN_TEXELS_PER_METRE),
  };

  const texture = new DynamicTexture(
    transposed ? 'wall-panels-with-grain-transposed' : 'wall-panels-with-grain',
    size,
    scene,
    false,
  );
  const context = texture.getContext();

  // Near-white, not the wall's actual off-white: StandardMaterial multiplies diffuseTexture
  // against diffuseColor per pixel (the same relationship SceneRoot.gridTexture's own dark
  // background has with the floor material's diffuseColor), so this texture only needs to carry
  // *variation* — the wall's own colour comes from `SceneRoot.buildRoom`'s wallMaterial, as
  // before this texture existed.
  context.fillStyle = '#f2f1ee';
  context.fillRect(0, 0, size.width, size.height);

  // Fine grain: a deterministic per-pixel jitter across every channel identically (so it reads
  // as brightness variation, not colour noise). Kept subtle per GRAIN_AMPLITUDE's own comment.
  const image = context.getImageData(0, 0, size.width, size.height);
  const data = image.data;

  for (let y = 0; y < size.height; y += 1) {
    for (let x = 0; x < size.width; x += 1) {
      const jitter = (pseudoRandom01(x, y) - 0.5) * GRAIN_AMPLITUDE;
      const i = (y * size.width + x) * 4;
      data[i] = clampByte((data[i] ?? 0) + jitter);
      data[i + 1] = clampByte((data[i + 1] ?? 0) + jitter);
      data[i + 2] = clampByte((data[i + 2] ?? 0) + jitter);
    }
  }

  context.putImageData(image, 0, 0);

  // The panel seam, drawn last so grain never dims it into invisibility. A 1-texel line, faint
  // (SEAM_ALPHA) — see this function's own comment for why one line is enough, and why its axis
  // flips with `transposed`.
  context.strokeStyle = `rgba(0, 0, 0, ${SEAM_ALPHA})`;
  context.lineWidth = 1;
  context.beginPath();

  if (transposed) {
    context.moveTo(0.5, 0);
    context.lineTo(0.5, size.height);
  } else {
    context.moveTo(0, 0.5);
    context.lineTo(size.width, 0.5);
  }

  context.stroke();

  texture.update(false);
  return texture;
}

/** The named vocabulary itself. Add a new key here, with its own `WallTextureGenerator`, to
 *  give a future room a different wall texture — see this module's own top comment. */
export const WALL_TEXTURES: Readonly<Record<string, WallTextureGenerator>> = {
  'panels-with-grain': buildPanelsWithGrainTexture,
};

/** The key `resolveWallTextureGenerator`/`buildWallTexture` fall back to for any key not present
 *  in `WALL_TEXTURES` — including, today, every call, since nothing yet selects a key per room
 *  (see this module's own top comment). Must itself be a real key in `WALL_TEXTURES`. */
export const DEFAULT_WALL_TEXTURE_KEY = 'panels-with-grain';

/**
 * Looks a key up in a named registry, falling back to `registry[defaultKey]` rather than
 * throwing for a key the registry does not recognise — the same "unknown falls back to a sane
 * default" contract the node catalogue's own silhouette lookup uses (CLAUDE.md, "The node
 * catalogue is data, served"), so an older or newer client can never be handed a key it does not
 * know how to render and crash on it.
 *
 * A free-standing generic rather than inlined into `resolveWallTextureGenerator` below: with
 * `WALL_TEXTURES` holding exactly one entry today (this module's own top comment explains why),
 * "known key" and "default key" are the same key by construction, so a test against the real
 * registry alone cannot tell correct lookup-by-key apart from a bug that always returns the
 * fallback regardless of what was asked for — both produce an identical result on the only key
 * that exists. Pulling the algorithm out lets `WallTextures.test.ts` prove it red against a
 * synthetic two-entry registry, independent of how many real wall textures this file happens to
 * define right now.
 */
export function resolveFromNamedRegistry<T>(
  registry: Readonly<Record<string, T>>,
  defaultKey: string,
  key: string,
): T {
  const fallback = registry[defaultKey];

  if (fallback === undefined) {
    // Guards a registry's own default key against drifting away from a real entry in it — the
    // same class of self-check STAR_OFFSETS_METRES/STAR_COUNT use in SceneRoot.ts.
    throw new Error(`defaultKey (${defaultKey}) is not a key in the given registry.`);
  }

  return Object.prototype.hasOwnProperty.call(registry, key) ? registry[key]! : fallback;
}

/**
 * Resolves a wall texture key to its generator — see `resolveFromNamedRegistry` for the
 * fallback contract this follows.
 */
export function resolveWallTextureGenerator(key: string): WallTextureGenerator {
  return resolveFromNamedRegistry(WALL_TEXTURES, DEFAULT_WALL_TEXTURE_KEY, key);
}

/**
 * Builds the tile texture for a given key against a real `Scene`, falling back the same way
 * `resolveWallTextureGenerator` does. `SceneRoot.buildRoom` is the only caller today.
 *
 * `transposed` defaults to `false` (the orientation every existing call and test already
 * expects) and is forwarded straight to the resolved generator — see `WallTextureGenerator`'s
 * own doc comment for what it means and why it exists. `SceneRoot.buildRoom` calls this twice
 * per room, once for each value, because a north/south wall's visible face and an east/west
 * wall's visible face disagree about which texture axis is world height.
 */
export function buildWallTexture(key: string, scene: Scene, transposed = false): DynamicTexture {
  return resolveWallTextureGenerator(key)(scene, transposed);
}
